"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SlateFacade = void 0;
const slate_history_1 = require("slate-history");
const FromSlate_1 = require("./sync/FromSlate");
const toSlateNode_1 = require("./sync/toSlateNode");
const applyPatch_1 = require("./sync/applyPatch");
const positions_1 = require("./positions");
const slate_1 = require("slate");
/**
 * Attempt to extract a single {@link PeritextOperation} from Slate's current
 * operation batch. Returns `undefined` when the batch is too complex (anything
 * other than a single `insert_text` or `remove_text`), in which case the caller
 * should fall back to the full document merge path.
 */
const tryExtractPeritextOperation = (operations, editor, txt) => {
    if (operations.length !== 1)
        return;
    const op = operations[0];
    if (op.type === 'insert_text') {
        const gap = (0, positions_1.slatePointToGap)(txt, editor, op);
        if (gap < 0)
            return;
        return [gap, 0, op.text];
    }
    if (op.type === 'remove_text') {
        const text = op.text;
        // For single-character deletes only; multi-char could be a complex block join.
        if (text.length > 1)
            return;
        const gap = (0, positions_1.slatePointToGap)(txt, editor, op);
        if (gap < 0)
            return;
        return [gap, text.length, ''];
    }
    return;
};
/**
 * Slate.js implementation of {@link RichtextEditorFacade}. Connects a Slate.js
 * `Editor` to a json-joy JSON CRDT "peritext" node.
 *
 * Usage:
 *
 * ```ts
 * const editor = withReact(createEditor());
 * const peritextRef = () => model.s.toExt();
 * const facade = new SlateFacade(editor, peritextRef);
 * const unbind = PeritextBinding.bind(peritextRef, facade);
 * ```
 */
class SlateFacade {
    _enterRemote() {
        this._remoteCnt++;
    }
    _exitRemote() {
        queueMicrotask(() => {
            this._remoteCnt--;
        });
    }
    constructor(editor, peritext, opts = {}, defaultBlock = 'p') {
        this.editor = editor;
        this.peritext = peritext;
        this.opts = opts;
        /** Pending remote operations counter. */
        this._remoteCnt = 0;
        this._disposed = false;
        this._toSlate = new toSlateNode_1.ToSlateNode(defaultBlock);
        // Optionally install slate-history plugin.
        const { history: historyOpt } = opts;
        const installHistory = historyOpt === true ? true : historyOpt === false ? false : !slate_history_1.HistoryEditor.isHistoryEditor(editor);
        if (installHistory)
            (0, slate_history_1.withHistory)(editor);
        this._origOnChange = editor.onChange;
        editor.onChange = this._slateOnChange = (options) => {
            if (this._disposed || !!this._remoteCnt) {
                this._origOnChange?.call(editor);
                return;
            }
            const operations = editor.operations;
            const hasDocChange = operations.some((op) => op.type !== 'set_selection');
            if (hasDocChange) {
                let simpleOperation;
                try {
                    const txt = this.peritext().txt;
                    simpleOperation = tryExtractPeritextOperation(operations, editor, txt);
                }
                catch { }
                this.onchange?.(simpleOperation);
            }
            else {
                this.onselection?.();
            }
            // Call _origOnChange (Slate React's internal handler) AFTER syncing
            // the Peritext model. This ensures that any callbacks triggered by
            // Slate React (e.g. sendLocalPresence) see the up-to-date model
            // when converting positions between Slate and CRDT coordinate spaces.
            this._origOnChange?.call(editor);
        };
    }
    get() {
        if (this._disposed)
            return ['', 0, []];
        const editor = this.editor;
        const children = editor.children;
        return FromSlate_1.FromSlate.convert(children, {
            isInline: (element) => {
                try {
                    return editor.isInline?.(element) ?? false;
                }
                catch {
                    return false;
                }
            },
        });
    }
    set(fragment) {
        if (this._disposed)
            return;
        const newChildren = this._toSlate.convert(fragment);
        if (!newChildren.length)
            return;
        const editor = this.editor;
        this._enterRemote();
        try {
            const doApply = () => {
                (0, applyPatch_1.applyPatch)(editor, newChildren);
                editor.onChange();
            };
            // Exclude remote changes from the undo stack so that undo/redo only
            // reverses local edits.
            if (slate_history_1.HistoryEditor.isHistoryEditor(editor)) {
                slate_history_1.HistoryEditor.withoutSaving(editor, doApply);
            }
            else {
                doApply();
            }
        }
        finally {
            this._exitRemote();
        }
    }
    getSelection() {
        if (this._disposed)
            return;
        const editor = this.editor;
        const selection = editor.selection;
        if (!selection)
            return;
        const txt = this.peritext().txt;
        const p1 = (0, positions_1.slatePointToPoint)(txt, editor, selection.anchor);
        const p2 = (0, positions_1.slatePointToPoint)(txt, editor, selection.focus);
        const range = txt.rangeFromPoints(p1, p2);
        const startIsAnchor = isBeforeOrEqual(selection.anchor, selection.focus);
        return [range, startIsAnchor];
    }
    setSelection(peritext, range, startIsAnchor) {
        if (this._disposed)
            return;
        const editor = this.editor;
        const txt = peritext.txt;
        const rootBlock = txt.blocks.root;
        const anchorPoint = startIsAnchor ? range.start : range.end;
        const headPoint = startIsAnchor ? range.end : range.start;
        const anchor = (0, positions_1.pointToSlatePoint)(rootBlock, anchorPoint, editor);
        const focus = (0, positions_1.pointToSlatePoint)(rootBlock, headPoint, editor);
        try {
            if (!validatePoint(editor, anchor) || !validatePoint(editor, focus)) {
                return;
            }
        }
        catch {
            return;
        }
        this._enterRemote();
        try {
            slate_1.Transforms.select(editor, { anchor, focus });
        }
        finally {
            this._exitRemote();
        }
    }
    dispose() {
        if (this._disposed)
            return;
        this._disposed = true;
        const e = this.editor;
        if (this._origOnChange && e.onChange === this._slateOnChange)
            e.onChange = this._origOnChange;
    }
}
exports.SlateFacade = SlateFacade;
/** Validate that a Slate point references a valid location within the editor
 * tree. Returns `false` if the path is out of bounds or points to a non-text node. */
const validatePoint = (editor, point) => {
    const { path, offset } = point;
    let node = editor;
    for (const idx of path) {
        const children = node.children;
        if (!children || idx >= children.length)
            return false;
        node = children[idx];
    }
    if (typeof node.text !== 'string')
        return false;
    if (offset > node.text.length)
        return false;
    return true;
};
/** Returns true if point `a` is before or equal to point `b`. */
const isBeforeOrEqual = (a, b) => {
    const aPath = a.path;
    const bPath = b.path;
    const len = Math.min(aPath.length, bPath.length);
    for (let i = 0; i < len; i++) {
        if (aPath[i] < bPath[i])
            return true;
        if (aPath[i] > bPath[i])
            return false;
    }
    if (aPath.length !== bPath.length)
        return aPath.length < bPath.length;
    return a.offset <= b.offset;
};
